/**
 * REF-5 — the Refund History block in the order-detail drawer.
 *
 * Sits directly below the Financial Breakdown and answers the guest-dispute question
 * without a support call: when it happened, who did it, what kind of refund it was and why,
 * what was returned, and TO WHICH CARD. The per-tender breakdown is the part that closes the
 * dispute — an order paid across two cards must show which card got which share, and the
 * shares must visibly add up to the refund total.
 *
 * An order can be refunded more than once (e.g. an item, then a fee, each with its own
 * reason), so the When/Processed by/Type/Reason group repeats once per `refund.details` entry.
 * Components and tenders below are order-wide and render once.
 */
import { T } from '../reportTokens';
import { fmtCurrency } from '../../drillHelpers';
import { RefundTypeBadge } from './RefundSections';
import {
    REFUND_COMPONENT_KEYS,
    REFUND_TYPE_LABEL,
    WATERFALL_POSTING,
    refundStatusLabel,
    refundTotal,
    type RefundRecord,
} from './refundModel';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'Sep 15, 2026 · 12:11 PM', read straight off the stored text.
 *
 * Deliberately NOT `new Date(...)`: the timestamp is the store's local wall-clock time, and
 * converting it through the viewer's timezone would show a manager in another zone a
 * different time from the one on the till receipt the guest is holding.
 */
function formatRefundTimestamp(rec: { createdAt: string; refundedAt: string }): string {
    const m = String(rec.createdAt ?? '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return rec.refundedAt;
    const [, y, mo, d, hh, mm] = m;
    const h = Number(hh);
    const time = `${h % 12 === 0 ? 12 : h % 12}:${mm} ${h < 12 ? 'AM' : 'PM'}`;
    return `${MONTHS[Number(mo) - 1] ?? mo} ${Number(d)}, ${y} · ${time}`;
}

export function RefundHistoryBlock({ refund }: {
    refund: RefundRecord | null;
}) {
    if (!refund) return null;

    const total = refundTotal(refund.components);
    const components = REFUND_COMPONENT_KEYS
        .map((k) => ({ key: k, label: WATERFALL_POSTING[k].label, value: refund.components[k] }))
        .filter((c) => c.value > 0);
    const returned = Number(refund.tenders.reduce((s, t) => s + (Number(t.amount) || 0), 0).toFixed(2));

    const label: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.4 };
    const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: 12, color: T.t2 };
    const amount: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', color: T.t1, fontWeight: 600, whiteSpace: 'nowrap' };
    const section: React.CSSProperties = { marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}` };

    // One group per refund action (never deduped). Falls back to the record's own single
    // event when the API sent no `refundDetails` (legacy shape).
    const events = refund.details?.length
        ? refund.details
        : [{ refundedAt: refund.refundedAt, createdAt: refund.createdAt, staffName: refund.staffName, type: refund.type, reason: refund.reason }];

    const factsOf = (e: (typeof events)[number]): Array<{ k: string; v: React.ReactNode }> => [
        { k: 'When', v: formatRefundTimestamp(e) },
        { k: 'Processed by', v: e.staffName },
        { k: 'Type', v: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><RefundTypeBadge type={e.type} />{REFUND_TYPE_LABEL[e.type]} refund</span> },
        { k: 'Reason', v: e.reason },
    ];

    return (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <span style={{ ...label, fontSize: 11 }}>Refund History</span>
                <span style={{ fontSize: 10.5, color: refund.type === 'full' ? T.red : T.orange, fontWeight: 600 }}>
                    {refundStatusLabel(refund.type)}
                </span>
            </div>

            <div style={{ background: T.bg, border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: '10px 12px' }}>
                {/* When · who · what kind · why */}
                {events.map((e, i) => (
                    <div key={i} style={i === 0 ? undefined : section}>
                        {events.length > 1 && (
                            <div style={{ ...label, marginBottom: 2 }}>Refund {i + 1} of {events.length}</div>
                        )}
                        {factsOf(e).map((f) => (
                            <div key={f.k} style={{ ...row, alignItems: 'center' }}>
                                <span style={{ color: T.t3 }}>{f.k}</span>
                                <span style={{ color: T.t1, fontWeight: 500, textAlign: 'right' }}>{f.v}</span>
                            </div>
                        ))}
                    </div>
                ))}

                {/* What was returned */}
                <div style={section}>
                    <div style={{ ...label, marginBottom: 4 }}>Returned</div>
                    {components.map((c) => (
                        <div key={c.key} style={row}>
                            <span>{c.label}</span>
                            <span style={amount}>{fmtCurrency(c.value)}</span>
                        </div>
                    ))}
                    <div style={{ ...row, borderTop: `1px solid ${T.divider}`, marginTop: 4, paddingTop: 8 }}>
                        <span style={{ fontWeight: 700, color: T.t1 }}>Refund total</span>
                        <span style={{ ...amount, color: T.red, fontWeight: 700 }}>{fmtCurrency(total)}</span>
                    </div>
                </div>

                {/* To which tender — one row per original tender */}
                <div style={section}>
                    <div style={{ ...label, marginBottom: 4 }}>Returned to</div>
                    {refund.tenders.map((t) => (
                        <div key={t.tender} style={row}>
                            <span>{t.tender}</span>
                            <span style={amount}>{fmtCurrency(t.amount)}</span>
                        </div>
                    ))}
                    {/* The tender total is shown, not just checked, so a reader can see the
                        shares add up to the refund total without doing the sum. */}
                    <div style={{ ...row, borderTop: `1px solid ${T.divider}`, marginTop: 4, paddingTop: 8 }}>
                        <span style={{ fontWeight: 700, color: T.t1 }}>
                            Total returned{refund.tenders.length > 1 ? ` · ${refund.tenders.length} tenders` : ''}
                        </span>
                        <span style={{ ...amount, fontWeight: 700 }}>{fmtCurrency(returned)}</span>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default RefundHistoryBlock;
